import React, { useEffect, useState } from "react";
import { DatePicker } from "@mui/x-date-pickers";
import MuiBox from "../MuiBox";
import MuiTypography from "../MuiTypography";
import FormError from "../FormError";
import CalendarIcon from "assets/images/svg-icons/calendar-01.svg";

const CustomDatePicker = ({
  label,
  value,
  onChange,
  views,
  error,
  errorMsg,
  onError,
  inputReadOnly = false,
  required = false,
  isBorderColor = false,
  format = "",
  disableFuture = false,
  maxDate,
  minDate,
  ...rest
}) => {
  const [showMsg, setShowMsg] = useState("");
  useEffect(() => {
    if (error) {
      setShowMsg(errorMsg);
    } else {
      setShowMsg("");
    }
  }, [error, errorMsg]);

  // Default to all views if none are provided
  const resolvedViews = views || ["year", "month", "day"];

  // Determine the appropriate format based on resolvedViews
  const getFormat = () => {
    if (resolvedViews.length === 3) return "DD-MM-YYYY"; // Full date format
    if (resolvedViews.includes("day") && resolvedViews.includes("month"))
      return "DD MMMM"; // Day and month
    if (resolvedViews.includes("month") && resolvedViews.includes("year"))
      return "MMMM YYYY"; // Month and year
    if (resolvedViews.includes("day")) return "DD"; // Only day
    if (resolvedViews.includes("month")) return "MMMM"; // Only month
    if (resolvedViews.includes("year")) return "YYYY"; // Only year
    return "DD MMMM YYYY"; // Fallback to full date
  };

  return (
    <MuiBox
      sx={{
        "& .MuiFormLabel-root": {
          color: "#25335E",
          fontWeight: 500,
        },
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: "#25335E !important",
        },
      }}
    >
      <MuiBox
        sx={{
          mt: "0",
        }}
      >
        {label && (
          <MuiBox
            sx={{
              mb: "6px",
            }}
          >
            <MuiTypography
              variant="14"
              fontWeight={500}
              color="text.darkBlack"
              sx={{ display: "flex", gap: "5px" }}
            >
              {label}
              <MuiTypography sx={{ color: "#FF4D4D !important" }}>
                {required && " *"}
              </MuiTypography>
            </MuiTypography>
          </MuiBox>
        )}
        <DatePicker
          disableFuture={disableFuture}
          maxDate={maxDate}
          minDate={minDate}
          value={value}
          slots={{
            openPickerIcon: () => (
              <img
                src={CalendarIcon}
                alt="CalendarIcon"
                height={20}
                width={20}
              />
            ),
          }}
          onChange={onChange}
          views={resolvedViews}
          format={format ? format : getFormat()}
          onError={(e) => {
            if (onError) {
              onError(e);
            }
            if (
              e === "invalidDate" ||
              e === "disableFuture" ||
              e === "disablePast"
            ) {
              setShowMsg("Please enter valid Date");
            } else if (e === "maxDate " || e === "minDate") {
              setShowMsg("Invalid Date Range");
            } else {
              setShowMsg(errorMsg);
            }
          }}
          slotProps={{
            textField: {
              error: error,
              InputProps: {
                readOnly: inputReadOnly,
              },
            },

            popper: {
              sx: {
                "& .MuiDayCalendar-root": {
                  padding: "0 16px",
                },

                "& .MuiDayCalendar-header": {
                  display: "grid",
                  gridTemplateColumns: "repeat(7, 1fr)",
                  textAlign: "center",
                  marginBottom: "4px",
                },

                "& .MuiDayCalendar-weekContainer": {
                  display: "grid",
                  gridTemplateColumns: "repeat(7, 1fr)",
                },

                "& .MuiPickersDay-root": {
                  margin: "0 auto",
                },
              },
            },
          }}
          sx={{
            borderRadius: "12px",
            width: "100%",
            bgcolor: "transparent",

            "& .MuiInputBase-input": {
              p: "14px",
              fontSize: "14px",
              color: "#000",
            },
            "& .MuiOutlinedInput-root": {
              "& .MuiOutlinedInput-notchedOutline": {
                border: "1px solid !important",

                borderColor: error
                  ? "#E12121 !important"
                  : isBorderColor
                    ? "#E6E6E6 !important"
                    : "transparent !important",
                boxShadow: error
                  ? "0px 0px 0px 2px #FFC9C9"
                  : "transparent !important",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: error
                  ? "#E12121 !important"
                  : "#1D4ED8 !important",
                boxShadow: error
                  ? "0px 0px 0px 2px #FFC9C9"
                  : "0px 0px 0px 2px #DBEAFE",
              },
            },
          }}
          {...rest}
        />
        {showMsg && <FormError errorMsg={showMsg} />}
      </MuiBox>
    </MuiBox>
  );
};

export default CustomDatePicker;
